import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Alert, Button, Collapse, Popconfirm, Progress, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { renderExperimentsApi } from '../services/catalogServiceAPI/renderExperimentsProxy';
import { StatusTag, armTotals, describeArm, describeCriterion, experimentTotals, formatDate } from './shared';

const { Title, Text, Paragraph } = Typography;
const POLL_MS = 5000;

export function RenderExperimentDetail() {
    const history = useHistory();
    const { id } = useParams();
    const [exp, setExp] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => renderExperimentsApi.get(id).then(setExp).catch((e) => message.error(e.message)), [id]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (exp?.status !== 'running') return undefined;
        const timer = setInterval(load, POLL_MS);
        return () => clearInterval(timer);
    }, [exp?.status, load]);

    const start = async () => {
        setBusy(true);
        try {
            setExp({ ...(await renderExperimentsApi.start(id)), failedCandidates: [] });
            message.success(exp.status === 'draft' ? 'Experiment started' : 'Resumed pending and failed candidates');
        } catch (e) {
            message.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        try {
            await renderExperimentsApi.remove(id);
            history.push('/experiments');
        } catch (e) {
            message.error(e.message);
        }
    };

    if (!exp) return <div style={{ padding: 24 }}><Spin /></div>;

    const totals = experimentTotals(exp);
    const isDraft = exp.status === 'draft';
    const canResume = !isDraft && (totals.pending > 0 || totals.failed > 0);
    const armLabel = new Map(exp.arms.map((a) => [a.id, a.label]));

    const armColumns = [
        { title: 'Label', dataIndex: 'label' },
        { title: 'Setup', render: (_, a) => describeArm(a) },
        {
            title: 'Progress', width: 260,
            render: (_, a) => {
                const t = armTotals(exp.progress, a.id);
                if (!t.total) return <Text type="secondary">not started</Text>;
                return (
                    <Space direction="vertical" size={0} style={{ width: '100%' }}>
                        <Progress percent={Math.round((t.done / t.total) * 100)} size="small" status={t.failed ? 'exception' : undefined} />
                        <Text type="secondary" style={{ fontSize: 12 }}>{t.done} done · {t.running} running · {t.pending} pending · {t.failed} failed</Text>
                    </Space>
                );
            },
        },
    ];

    const failedColumns = [
        { title: 'Source', dataIndex: 'sourceRenderingId', width: 320, render: (v) => <Text code>{v}</Text> },
        { title: 'Arm', dataIndex: 'armId', width: 140, render: (v) => armLabel.get(v) || v },
        { title: 'Error', dataIndex: 'error' },
    ];

    return (
        <div style={{ padding: 24, maxWidth: 1100 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                    <div>
                        <Space align="center">
                            <Title level={3} style={{ margin: 0 }}>{exp.name}</Title>
                            <StatusTag status={exp.status} />
                        </Space>
                        <div><Text type="secondary">Created {formatDate(exp.createdAt)}{exp.createdBy ? ` by ${exp.createdBy}` : ''}</Text></div>
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary">Testing: </Text>
                            {(exp.criteria || []).map((c) => <Tag key={c.id}>{describeCriterion(c)}</Tag>)}
                            <Tag color="blue">Duration</Tag>
                            <Tag color="blue">Failure rate</Tag>
                        </div>
                        {exp.description && <Paragraph style={{ marginTop: 8 }}>{exp.description}</Paragraph>}
                    </div>
                    <Space wrap>
                        {isDraft && <Button onClick={() => history.push(`/experiments/${id}/edit`)}>Edit</Button>}
                        {isDraft && (
                            <Popconfirm title="Delete this draft?" onConfirm={remove}>
                                <Button danger>Delete</Button>
                            </Popconfirm>
                        )}
                        {(isDraft || canResume) && (
                            <Button type="primary" loading={busy} onClick={start}>{isDraft ? 'Start generating' : 'Resume'}</Button>
                        )}
                        {!isDraft && <Button onClick={() => history.push(`/experiments/${id}/rate`)}>Rate</Button>}
                        {!isDraft && <Button onClick={() => history.push(`/experiments/${id}/results`)}>Results</Button>}
                    </Space>
                </div>

                {isDraft && (
                    <Alert
                        type="info"
                        showIcon
                        message={exp.sourceFilter?.renderingIds
                            ? `Will use the ${exp.sourceCount} hand-picked rendering${exp.sourceCount === 1 ? '' : 's'}. Starting freezes the source set.`
                            : `Will use the latest ${exp.sourceCount} eligible renderings${exp.sourceFilter?.timeOfDay ? ` (${exp.sourceFilter.timeOfDay})` : ''}${exp.sourceFilter?.environment ? ` (${exp.sourceFilter.environment})` : ''}. Starting freezes the source set.`}
                    />
                )}
                {!isDraft && (
                    <Alert
                        type={totals.failed ? 'warning' : 'info'}
                        showIcon
                        message={`${exp.progress.sources} sources · ${totals.done}/${totals.total} candidates generated · ${exp.progress.votes} votes${totals.failed ? ` · ${totals.failed} failed (Resume retries them)` : ''}`}
                    />
                )}

                <Table dataSource={exp.arms} columns={armColumns} rowKey="id" size="small" pagination={false} />

                <Collapse
                    items={exp.arms.filter((a) => a.kind === 'generate').map((a) => ({
                        key: a.id,
                        label: `Prompt template — ${a.label}`,
                        children: <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, margin: 0 }}>{a.promptTemplate || '(production prompt)'}</pre>,
                    }))}
                />

                {exp.failedCandidates?.length > 0 && (
                    <div>
                        <Title level={5}>Failed candidates</Title>
                        <Table dataSource={exp.failedCandidates} columns={failedColumns} rowKey="id" size="small" pagination={{ pageSize: 10 }} />
                    </div>
                )}
            </Space>
        </div>
    );
}
